import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  ArrowLeft, ShoppingCart, MapPin, Phone, User, Mail, 
  CreditCard, Banknote, QrCode, Truck, Package, Tag, 
  CheckCircle, AlertCircle, Loader2, Copy, Clock, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CheckoutPage() {
  const { storeSlug } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [cart, setCart] = useState([]);
  const [pricing, setPricing] = useState(null);
  const [menu, setMenu] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState(null);
  
  // Form State
  const [orderType, setOrderType] = useState('DELIVERY');
  const [customerName, setCustomerName] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerCPF, setCustomerCPF] = useState('');  // CPF para rastreamento
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [notes, setNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('COD');
  const [codPaymentType, setCodPaymentType] = useState('');  // DINHEIRO, CARTAO_ENTREGA
  const [needsChange, setNeedsChange] = useState(false);
  const [changeFor, setChangeFor] = useState('');
  
  // PIX Payment State
  const [showPixModal, setShowPixModal] = useState(false);
  const [pixData, setPixData] = useState(null);
  const [pixPaymentId, setPixPaymentId] = useState(null);
  const [checkingPixStatus, setCheckingPixStatus] = useState(false);
  
  // Card Payment State
  const [showCardModal, setShowCardModal] = useState(false);
  const [cardData, setCardData] = useState({
    number: '',
    name: '',
    expiry: '',
    cvv: '',
    installments: 1,
    cpf: ''
  });
  const [processingCard, setProcessingCard] = useState(false);
  const [pendingOrderId, setPendingOrderId] = useState(null);
  const [cardBrand, setCardBrand] = useState(null);
  const [mpPaymentMethodId, setMpPaymentMethodId] = useState(null);
  const [mpIssuerId, setMpIssuerId] = useState(null);
  const [binResolved, setBinResolved] = useState(false);
  const [loadingBin, setLoadingBin] = useState(false);
  const binLookupTimer = React.useRef(null);
  
  // Address State
  const [address, setAddress] = useState({
    street: '',
    number: '',
    complement: '',
    neighborhood: '',
    city: '',
    state: '',
    cep: '',
    lat: null,
    lng: null
  });
  const [cepLoading, setCepLoading] = useState(false);
  const [googleDistance, setGoogleDistance] = useState(null);
  const googleMapsLoaded = useRef(false);

  // Load Google Maps script for Distance Matrix
  const loadGoogleMaps = () => {
    return new Promise((resolve) => {
      if (window.google && window.google.maps) { resolve(); return; }
      if (googleMapsLoaded.current) { resolve(); return; }
      googleMapsLoaded.current = true;
      const script = document.createElement('script');
      script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyBslmdWaiWkD85mWtZY8loRATqVgKM_K6g&libraries=geometry';
      script.async = true;
      script.onload = resolve;
      script.onerror = resolve; // continue even if fails
      document.head.appendChild(script);
    });
  };

  // Calculate Google Maps driving distance
  const getGoogleMapsDistance = async (destLat, destLng, originLat, originLng) => {
    try {
      await loadGoogleMaps();
      if (!window.google || !window.google.maps) return null;
      
      const service = new window.google.maps.DistanceMatrixService();
      return new Promise((resolve) => {
        service.getDistanceMatrix({
          origins: [new window.google.maps.LatLng(originLat, originLng)],
          destinations: [new window.google.maps.LatLng(destLat, destLng)],
          travelMode: window.google.maps.TravelMode.DRIVING,
        }, (response, status) => {
          if (status === 'OK' && response.rows[0]?.elements[0]?.status === 'OK') {
            const distMeters = response.rows[0].elements[0].distance.value;
            resolve(distMeters / 1000); // convert to km
          } else {
            resolve(null);
          }
        });
      });
    } catch {
      return null;
    }
  };

  // Geocode address using Google Maps (primary) or Nominatim (fallback)
  const geocodeAddress = async (street, number, city, state) => {
    // Try Google Maps first
    try {
      await loadGoogleMaps();
      if (window.google && window.google.maps) {
        const geocoder = new window.google.maps.Geocoder();
        const addr = `${street}, ${number}, ${city}, ${state}, Brasil`;
        return new Promise((resolve) => {
          geocoder.geocode({ address: addr }, (results, status) => {
            if (status === 'OK' && results[0]) {
              resolve({
                lat: results[0].geometry.location.lat(),
                lng: results[0].geometry.location.lng(),
              });
            } else {
              resolve(null);
            }
          });
        });
      }
    } catch {}
    // Fallback to Nominatim
    try {
      const addr = `${street}, ${number}, ${city}, ${state}, Brasil`;
      const resp = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}&limit=1&countrycodes=br`);
      const data = await resp.json();
      if (data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
    } catch {}
    return null;
  };

  const handleCepSearch = async (cepValue) => {
    const cep = (cepValue || address.cep).replace(/\D/g, '');
    if (cep.length !== 8) return;
    setCepLoading(true);
    try {
      const resp = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await resp.json();
      if (data.erro) { toast.error('CEP não encontrado'); return; }
      const newAddr = {
        ...address, cep: cepValue || address.cep,
        street: data.logradouro || address.street,
        neighborhood: data.bairro || address.neighborhood,
        city: data.localidade || address.city,
        state: data.uf || address.state,
      };
      setAddress(newAddr);
      toast.success('Endereço encontrado!');
      // Auto-geocodificar com Google Maps
      if (data.logradouro) {
        const coords = await geocodeAddress(
          data.logradouro,
          address.number || '1',
          data.localidade,
          data.uf
        );
        if (coords) {
          setAddress(prev => ({ ...prev, lat: coords.lat, lng: coords.lng }));
        }
      }
    } catch { toast.error('Erro ao buscar CEP'); }
    finally { setCepLoading(false); }
  };

  useEffect(() => {
    loadCart();
    fetchMenuInfo();
    fetchPaymentConfig();
  }, [storeSlug]);

  useEffect(() => {
    if (cart.length > 0) {
      calculatePricing();
    }
  }, [cart, orderType, couponApplied, address.neighborhood, address.lat, address.lng]);

  // Polling para verificar status do PIX
  useEffect(() => {
    let interval;
    if (pixPaymentId && showPixModal) {
      interval = setInterval(() => {
        checkPixPaymentStatus();
      }, 5000); // Verifica a cada 5 segundos
    }
    return () => clearInterval(interval);
  }, [pixPaymentId, showPixModal]);

  const loadCart = () => {
    const savedCart = localStorage.getItem(`cart_${storeSlug}`);
    if (savedCart) {
      setCart(JSON.parse(savedCart));
    } else {
      navigate(`/menu/${storeSlug}`);
    }
  };

  const fetchMenuInfo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/stores/${storeSlug}/menu`);
      if (response.ok) {
        const data = await response.json();
        setMenu(data);
      }
    } catch (err) {
      console.error('Error fetching menu info:', err);
    }
  };

  const fetchPaymentConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/public/stores/${storeSlug}/payment-config`);
      if (response.ok) {
        const data = await response.json();
        setPaymentConfig(data);
      }
    } catch (err) {
      console.error('Error fetching payment config:', err);
    }
  };

  const checkPixPaymentStatus = async () => {
    if (!pixPaymentId || checkingPixStatus) return;
    
    setCheckingPixStatus(true);
    try {
      const response = await fetch(`${API_URL}/api/public/pix/status/${pixPaymentId}`);
      if (response.ok) {
        const data = await response.json();
        if (data.status === 'approved') {
          toast.success('Pagamento PIX confirmado!');
          setShowPixModal(false);
          localStorage.removeItem(`cart_${storeSlug}`);
          navigate(`/menu/${storeSlug}/order-success?payment=pix`);
        }
      }
    } catch (err) {
      console.error('Error checking PIX status:', err);
    } finally {
      setCheckingPixStatus(false);
    }
  };

  const copyPixCode = () => {
    if (pixData?.qr_code) {
      navigator.clipboard.writeText(pixData.qr_code);
      toast.success('Código PIX copiado!');
    }
  };

  // Detectar bandeira do cartão
  const detectCardBrand = (number) => {
    const cleanNumber = number.replace(/\D/g, '');
    if (/^4/.test(cleanNumber)) return 'visa';
    if (/^5[1-5]/.test(cleanNumber)) return 'mastercard';
    if (/^3[47]/.test(cleanNumber)) return 'amex';
    if (/^6(?:011|5)/.test(cleanNumber)) return 'discover';
    if (/^(?:2131|1800|35)/.test(cleanNumber)) return 'jcb';
    if (/^3(?:0[0-5]|[68])/.test(cleanNumber)) return 'diners';
    if (/^(636368|636369|438935|504175|451416|636297|5067|4576|4011)/.test(cleanNumber)) return 'elo';
    if (/^(606282|3841)/.test(cleanNumber)) return 'hipercard';
    return null;
  };

  // Formatar número do cartão
  const formatCardNumber = (value) => {
    const v = value.replace(/\D/g, '').substring(0, 16);
    const parts = [];
    for (let i = 0; i < v.length; i += 4) {
      parts.push(v.substring(i, i + 4));
    }
    return parts.join(' ');
  };

  // Formatar data de validade
  const formatExpiry = (value) => {
    const v = value.replace(/\D/g, '').substring(0, 4);
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2);
    }
    return v;
  };

  // Formatar CPF
  const formatCPF = (value) => {
    const v = value.replace(/\D/g, '').substring(0, 11);
    if (v.length <= 3) return v;
    if (v.length <= 6) return v.substring(0, 3) + '.' + v.substring(3);
    if (v.length <= 9) return v.substring(0, 3) + '.' + v.substring(3, 6) + '.' + v.substring(6);
    return v.substring(0, 3) + '.' + v.substring(3, 6) + '.' + v.substring(6, 9) + '-' + v.substring(9);
  };

  // Processar pagamento com cartão
  const processCardPayment = async () => {
    // Validações
    if (!cardData.number || cardData.number.replace(/\D/g, '').length < 13) {
      toast.error('Número do cartão inválido');
      return;
    }
    if (!cardData.name.trim()) {
      toast.error('Nome do titular é obrigatório');
      return;
    }
    if (!cardData.expiry || cardData.expiry.length < 5) {
      toast.error('Data de validade inválida');
      return;
    }
    if (!cardData.cvv || cardData.cvv.length < 3) {
      toast.error('CVV inválido');
      return;
    }
    if (!cardData.cpf || cardData.cpf.replace(/\D/g, '').length < 11) {
      toast.error('CPF é obrigatório para pagamentos com cartão');
      return;
    }

    // GUARD: BIN deve estar resolvido antes de submeter
    if (loadingBin) {
      toast.info('Identificando bandeira do cartao... Aguarde um instante.');
      return;
    }
    if (!mpPaymentMethodId) {
      // Tentar resolver novamente
      lookupPaymentMethod(cardData.number);
      toast.error('Nao foi possivel identificar a bandeira. Verifique o numero do cartao.');
      return;
    }

    setProcessingCard(true);
    
    try {
      // Carregar SDK do Mercado Pago se não estiver carregado
      if (!window.MercadoPago && paymentConfig?.public_key) {
        await loadMercadoPagoSDK();
      }

      if (!window.MercadoPago) {
        throw new Error('SDK do Mercado Pago não carregado');
      }

      const mp = new window.MercadoPago(paymentConfig.public_key);
      
      // Extrair mês e ano
      const [expMonth, expYear] = cardData.expiry.split('/');
      
      // Criar token do cartão
      const cardToken = await mp.createCardToken({
        cardNumber: cardData.number.replace(/\D/g, ''),
        cardholderName: cardData.name,
        cardExpirationMonth: expMonth,
        cardExpirationYear: '20' + expYear,
        securityCode: cardData.cvv,
        identificationType: 'CPF',
        identificationNumber: cardData.cpf.replace(/\D/g, '')
      });

      if (cardToken.error) {
        throw new Error(cardToken.error);
      }

      // Enviar pagamento para o backend
      const response = await fetch(`${API_URL}/api/public/card/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_slug: storeSlug,
          order_id: pendingOrderId,
          amount: pricing?.total || getCartTotal(),
          token: cardToken.id,
          installments: parseInt(cardData.installments),
          payment_method_id: mpPaymentMethodId,
          issuer_id: mpIssuerId || undefined,
          payer_email: customerEmail,
          payer_name: cardData.name,
          payer_cpf: cardData.cpf.replace(/\D/g, '')
        })
      });

      const result = await response.json();

      if (!response.ok) {
        // Tratar erros específicos do Mercado Pago
        let errorMsg = result.detail || 'Erro ao processar pagamento';
        if (errorMsg.includes('bin_not_found')) {
          errorMsg = 'Numero do cartao invalido. Verifique os dados e tente novamente.';
        } else if (errorMsg.includes('invalid_token')) {
          errorMsg = 'Dados do cartao invalidos. Verifique e tente novamente.';
        } else if (errorMsg.includes('diff_param_bins') || errorMsg.includes('10103')) {
          errorMsg = 'Erro na identificacao da bandeira. Apague o numero e digite novamente.';
          setMpPaymentMethodId(null);
          setBinResolved(false);
        }
        throw new Error(errorMsg);
      }

      if (result.success) {
        toast.success('Pagamento aprovado!');
        setShowCardModal(false);
        localStorage.removeItem(`cart_${storeSlug}`);
        // Redirecionar para página de acompanhamento
        navigate(`/track/${result.tracking_token}`);
      } else {
        toast.error(result.message || 'Pagamento não aprovado');
      }
    } catch (err) {
      console.error('Erro no pagamento:', err);
      toast.error(err.message || 'Erro ao processar pagamento com cartão');
    } finally {
      setProcessingCard(false);
    }
  };

  // Carregar SDK do Mercado Pago
  const loadMercadoPagoSDK = () => {
    return new Promise((resolve, reject) => {
      if (window.MercadoPago) {
        resolve();
        return;
      }
      
      const script = document.createElement('script');
      script.src = 'https://sdk.mercadopago.com/js/v2';
      script.onload = resolve;
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  // Carregar SDK quando tiver a public_key
  useEffect(() => {
    if (paymentConfig?.public_key && paymentConfig?.credit_card_enabled) {
  // Consultar BIN no MercadoPago para obter payment_method_id correto
  const lookupPaymentMethod = async (cardNumber, retryCount = 0) => {
    const cleanNum = cardNumber.replace(/\D/g, "");
    if (cleanNum.length < 6) {
      setMpPaymentMethodId(null);
      setMpIssuerId(null);
      setBinResolved(false);
      setLoadingBin(false);
      return;
    }
    if (cleanNum.length < 8) return;
    setLoadingBin(true);
    try {
      if (!window.MercadoPago && paymentConfig?.public_key) {
        await loadMercadoPagoSDK();
      }
      if (!window.MercadoPago || !paymentConfig?.public_key) {
        console.error("[MP] SDK nao carregado para BIN lookup");
        if (retryCount < 2) {
          setTimeout(() => lookupPaymentMethod(cardNumber, retryCount + 1), 1000);
        }
        return;
      }
      const mp = new window.MercadoPago(paymentConfig.public_key);
      const bin = cleanNum.substring(0, 8);
      const result = await mp.getPaymentMethods({ bin });
      const methods = Array.isArray(result) ? result : (result?.results || []);
      if (methods.length > 0) {
        const pmId = methods[0].id;
        const issuers = methods[0].issuer?.id || null;
        setMpPaymentMethodId(pmId);
        setMpIssuerId(issuers);
        setCardBrand(pmId);
        setBinResolved(true);
        setLoadingBin(false);
        console.log("[MP] BIN", bin, "-> payment_method_id:", pmId, "issuer:", issuers);
      } else {
        setMpPaymentMethodId(null);
        setBinResolved(false);
        setLoadingBin(false);
        console.warn("[MP] BIN", bin, "-> nenhum metodo encontrado");
      }
    } catch (err) {
      console.warn("[MP] Erro BIN lookup (tentativa " + (retryCount + 1) + "):", err);
      setMpPaymentMethodId(null);
      setBinResolved(false);
      if (retryCount < 2) {
        setTimeout(() => lookupPaymentMethod(cardNumber, retryCount + 1), 1500);
      } else {
        setLoadingBin(false);
      }
    }
  };

        loadMercadoPagoSDK().catch(err => {
        console.error('Erro ao carregar SDK do Mercado Pago:', err);
      });
    }
  }, [paymentConfig]);

  const calculatePricing = async () => {
    try {
      setLoading(true);
      
      // Try getting Google Maps distance if we have coords
      let gmDistance = googleDistance;
      if (orderType === 'DELIVERY' && address.lat && address.lng && !gmDistance) {
        try {
          // Fetch store origin coords from delivery config
          const configResp = await fetch(`${API_URL}/api/public/stores/${storeSlug}/delivery-config`);
          if (configResp.ok) {
            const configData = await configResp.json();
            if (configData.origin_lat && configData.origin_lng) {
              const dist = await getGoogleMapsDistance(address.lat, address.lng, configData.origin_lat, configData.origin_lng);
              if (dist) {
                gmDistance = Math.round(dist * 100) / 100;
                setGoogleDistance(gmDistance);
              }
            }
          }
        } catch (e) {
          console.log('Google Maps distance failed, backend will use haversine:', e);
        }
      }
      
      const response = await fetch(`${API_URL}/api/public/cart/price`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_slug: storeSlug,
          items: cart.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            notes: item.notes || null,
            modifiers: item.selectedOptions ? Object.entries(item.selectedOptions).flatMap(([groupId, opts]) =>
              (Array.isArray(opts) ? opts : [opts]).map(opt => ({
                group_id: groupId,
                group_name: item.optionGroupNames?.[groupId] || '',
                option_id: opt.id,
                option_name: opt.name,
                price_modifier: opt.price_modifier || 0
              }))
            ) : null
          })),
          coupon_code: couponApplied?.code || null,
          delivery_neighborhood: orderType === 'DELIVERY' ? address.neighborhood : null,
          delivery_lat: orderType === 'DELIVERY' ? address.lat : null,
          delivery_lng: orderType === 'DELIVERY' ? address.lng : null,
          delivery_distance_km: orderType === 'DELIVERY' ? gmDistance : null,
          order_type: orderType
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setPricing(data);
      }
    } catch (err) {
      console.error('Error calculating pricing:', err);
    } finally {
      setLoading(false);
    }
  };

  const validateCoupon = async () => {
    if (!couponCode.trim()) return;
    
    try {
      const response = await fetch(`${API_URL}/api/public/coupons/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_slug: storeSlug,
          coupon_code: couponCode,
          subtotal: pricing?.subtotal || 0,
          customer_phone: customerPhone
        })
      });
      
      const data = await response.json();
      
      if (data.valid) {
        setCouponApplied({
          code: data.coupon_code,
          discount: data.discount,
          description: data.description
        });
        toast.success(`Cupom aplicado: ${data.description}`);
      } else {
        toast.error(data.error || 'Cupom inválido');
        setCouponApplied(null);
      }
    } catch (err) {
      toast.error('Erro ao validar cupom');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatPhone = (value) => {
    // Remove non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as (XX) XXXXX-XXXX
    if (digits.length <= 2) return digits;
    if (digits.length <= 7) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7, 11)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validations
    if (!customerName.trim()) {
      toast.error('Informe seu nome');
      return;
    }
    if (!customerPhone.trim() || customerPhone.replace(/\D/g, '').length < 10) {
      toast.error('Informe um telefone válido');
      return;
    }
    if (orderType === 'DELIVERY') {
      if (!address.street || !address.number || !address.neighborhood) {
        toast.error('Preencha o endereço de entrega');
        return;
      }
    }
    if (menu?.min_order_value && pricing?.subtotal < menu.min_order_value) {
      toast.error(`Valor mínimo do pedido: ${formatCurrency(menu.min_order_value)}`);
      return;
    }
    
    // Para pagamento na entrega, validar tipo selecionado
    if (paymentMethod === 'COD' && !codPaymentType) {
      toast.error('Selecione como deseja pagar na entrega (Dinheiro ou Cartão)');
      return;
    }
    
    // Para pagamento online, validar email
    if ((paymentMethod === 'ONLINE_PIX' || paymentMethod === 'ONLINE_CARD') && !customerEmail) {
      toast.error('Informe seu email para pagamento online');
      return;
    }
    
    try {
      setSubmitting(true);

      // Revalidar status da loja antes de submeter (CARDÁPIO DIGITAL)
      try {
        const statusCheck = await fetch(`${API_URL}/api/public/stores/${storeSlug}/menu`);
        const menuData = await statusCheck.json();
        if (!menuData.is_open || !menuData.allow_order) {
          toast.error(menuData.store_status_message || "A loja esta fechada no momento. Nao e possivel finalizar o pedido.");
          setSubmitting(false);
          return;
        }
      } catch (checkErr) {
        console.error("Erro ao verificar status:", checkErr);
      }
      
      // Criar o pedido primeiro
      const response = await fetch(`${API_URL}/api/public/orders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          store_slug: storeSlug,
          items: cart.map(item => ({
            product_id: item.product_id,
            quantity: item.quantity,
            notes: item.notes || null,
            modifiers: item.selectedOptions ? Object.entries(item.selectedOptions).flatMap(([groupId, opts]) =>
              (Array.isArray(opts) ? opts : [opts]).map(opt => ({
                group_id: groupId,
                group_name: item.optionGroupNames?.[groupId] || '',
                option_id: opt.id,
                option_name: opt.name,
                price_modifier: opt.price_modifier || 0
              }))
            ) : null
          })),
          customer_name: customerName,
          customer_phone: customerPhone.replace(/\D/g, ''),
          customer_email: customerEmail || null,
          customer_cpf: customerCPF.replace(/\D/g, '') || null,  // CPF para rastreamento
          order_type: orderType,
          delivery_address: orderType === 'DELIVERY' ? {
            ...address,
            lat: address.lat || null,
            lng: address.lng || null,
            distance_km: googleDistance || null
          } : null,
          payment_method: paymentMethod,
          cod_payment_type: paymentMethod === "COD" ? codPaymentType : null,
          change_for: paymentMethod === "COD" && codPaymentType === "DINHEIRO" && needsChange ? parseFloat(changeFor) || null : null,
          coupon_code: couponApplied?.code || null,
          notes: notes || null
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao criar pedido');
      }
      
      const orderData = await response.json();
      
      // Se for pagamento online PIX, gerar QR Code
      if (paymentMethod === 'ONLINE_PIX') {
        const pixResponse = await fetch(`${API_URL}/api/public/pix/create`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            store_slug: storeSlug,
            order_id: orderData.order_id,
            amount: pricing?.total || getCartTotal(),
            payer_email: customerEmail,
            payer_name: customerName
          })
        });
        
        if (!pixResponse.ok) {
          const error = await pixResponse.json();
          throw new Error(error.detail || 'Erro ao gerar PIX');
        }
        
        const pix = await pixResponse.json();
        setPixData(pix.pix);
        setPixPaymentId(pix.payment_id);
        setShowPixModal(true);
        toast.success('PIX gerado! Escaneie o QR Code para pagar.');
        return;
      }
      
      // Se for cartão, mostrar modal de cartão
      if (paymentMethod === 'ONLINE_CARD') {
        setPendingOrderId(orderData.order_id);
        setShowCardModal(true);
        setSubmitting(false);
        return;
      }
      
      // Pagamento na entrega (COD)
      localStorage.removeItem(`cart_${storeSlug}`);
      toast.success('Pedido realizado com sucesso!');
      navigate(`/track/${orderData.tracking_token}`);
      
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const getCartTotal = () => {
    return cart.reduce((total, item) => total + ((item.price + (item.optionsTotal || 0)) * item.quantity), 0);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header 
        className="sticky top-0 z-40 shadow-md"
        style={{ backgroundColor: menu?.theme_color || '#FF6B6B' }}
      >
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20"
              onClick={() => navigate(`/menu/${storeSlug}`)}
            >
              <ArrowLeft className="w-6 h-6" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Finalizar Pedido</h1>
              <p className="text-sm text-white/80">{menu?.store_name}</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Order Type */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Truck className="w-5 h-5" />
                Tipo de Entrega
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={orderType}
                onValueChange={setOrderType}
                className="grid grid-cols-2 gap-4"
              >
                {menu?.delivery_enabled && (
                  <Label
                    htmlFor="delivery"
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      orderType === 'DELIVERY' ? 'border-orange-500 bg-orange-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <RadioGroupItem value="DELIVERY" id="delivery" />
                    <Truck className="w-5 h-5 text-slate-600" />
                    <span>Delivery</span>
                  </Label>
                )}
                {menu?.takeout_enabled && (
                  <Label
                    htmlFor="takeout"
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      orderType === 'TAKEOUT' ? 'border-orange-500 bg-orange-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <RadioGroupItem value="TAKEOUT" id="takeout" />
                    <Package className="w-5 h-5 text-slate-600" />
                    <span>Retirar no Local</span>
                  </Label>
                )}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Seus Dados
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="name">Nome *</Label>
                <Input
                  id="name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Seu nome completo"
                  required
                  data-testid="customer-name"
                />
              </div>
              
              <div>
                <Label htmlFor="phone">Telefone/WhatsApp *</Label>
                <Input
                  id="phone"
                  value={customerPhone}
                  onChange={(e) => setCustomerPhone(formatPhone(e.target.value))}
                  placeholder="(00) 00000-0000"
                  required
                  data-testid="customer-phone"
                />
              </div>
              
              <div>
                <Label htmlFor="email">E-mail (opcional)</Label>
                <Input
                  id="email"
                  type="email"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  placeholder="seu@email.com"
                  data-testid="customer-email"
                />
              </div>
              
              {/* CPF para rastreamento */}
              <div>
                <Label htmlFor="cpf">CPF (para acompanhar pedido)</Label>
                <Input
                  id="cpf"
                  value={customerCPF}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '');
                    if (v.length <= 11) {
                      let formatted = v;
                      if (v.length > 3) formatted = `${v.slice(0, 3)}.${v.slice(3)}`;
                      if (v.length > 6) formatted = `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6)}`;
                      if (v.length > 9) formatted = `${v.slice(0, 3)}.${v.slice(3, 6)}.${v.slice(6, 9)}-${v.slice(9)}`;
                      setCustomerCPF(formatted);
                    }
                  }}
                  placeholder="000.000.000-00"
                  data-testid="customer-cpf"
                />
                <p className="text-xs text-slate-500 mt-1">Opcional. Use para acompanhar seu pedido pelo CPF.</p>
              </div>
            </CardContent>
          </Card>

          {/* Delivery Address */}
          {orderType === 'DELIVERY' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5" />
                  Endereço de Entrega
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* CEP first */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="cep">CEP *</Label>
                    <div className="flex gap-2">
                      <Input
                        id="cep"
                        value={address.cep}
                        onChange={(e) => {
                          const v = e.target.value.replace(/\D/g, '').slice(0, 8);
                          setAddress({ ...address, cep: v });
                          if (v.length === 8) handleCepSearch(v);
                        }}
                        placeholder="00000000"
                        maxLength={9}
                        data-testid="address-cep"
                      />
                      {cepLoading && <Loader2 className="w-5 h-5 animate-spin text-slate-400 self-center" />}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="number">Número *</Label>
                    <Input
                      id="number"
                      value={address.number}
                      onChange={(e) => setAddress({ ...address, number: e.target.value })}
                      placeholder="123"
                      required
                      data-testid="address-number"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div className="col-span-2">
                    <Label htmlFor="street">Rua *</Label>
                    <Input
                      id="street"
                      value={address.street}
                      onChange={(e) => setAddress({ ...address, street: e.target.value })}
                      placeholder="Nome da rua"
                      required
                      data-testid="address-street"
                    />
                  </div>
                  <div>
                    <Label htmlFor="complement">Complemento</Label>
                    <Input
                      id="complement"
                      value={address.complement}
                      onChange={(e) => setAddress({ ...address, complement: e.target.value })}
                      placeholder="Apto, bloco..."
                      data-testid="address-complement"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="neighborhood">Bairro *</Label>
                    <Input
                      id="neighborhood"
                      value={address.neighborhood}
                      onChange={(e) => setAddress({ ...address, neighborhood: e.target.value })}
                      placeholder="Seu bairro"
                      required
                      data-testid="address-neighborhood"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Cidade</Label>
                    <Input
                      id="city"
                      value={address.city}
                      onChange={(e) => setAddress({ ...address, city: e.target.value })}
                      placeholder="Sua cidade"
                      data-testid="address-city"
                    />
                  </div>
                  <div>
                    <Label htmlFor="state">Estado</Label>
                    <Input
                      id="state"
                      value={address.state}
                      onChange={(e) => setAddress({ ...address, state: e.target.value })}
                      placeholder="SP"
                      data-testid="address-state"
                    />
                  </div>
                </div>

                {address.lat && address.lng && (
                  <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 rounded-lg px-3 py-2">
                    <CheckCircle className="w-4 h-4" />
                    Localização detectada
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Coupon */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5" />
                Cupom de Desconto
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-2">
                <Input
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Digite o código do cupom"
                  disabled={!!couponApplied}
                  data-testid="coupon-input"
                />
                {couponApplied ? (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setCouponApplied(null);
                      setCouponCode('');
                    }}
                  >
                    Remover
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={validateCoupon}
                    data-testid="apply-coupon-btn"
                  >
                    Aplicar
                  </Button>
                )}
              </div>
              
              {couponApplied && (
                <div className="mt-3 p-3 bg-green-50 rounded-lg flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-700 text-sm">
                    {couponApplied.description} - {formatCurrency(couponApplied.discount)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Payment Method */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="w-5 h-5" />
                Forma de Pagamento
              </CardTitle>
            </CardHeader>
            <CardContent>
              <RadioGroup
                value={paymentMethod}
                onValueChange={setPaymentMethod}
                className="space-y-3"
              >
                <Label
                  htmlFor="cod"
                  className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                    paymentMethod === 'COD' ? 'border-orange-500 bg-orange-50' : 'hover:bg-slate-50'
                  }`}
                >
                  <RadioGroupItem value="COD" id="cod" />
                  <Banknote className="w-5 h-5 text-slate-600" />
                  <div>
                    <p className="font-medium">Pagar na Entrega</p>
                    <p className="text-sm text-slate-500">Escolha como deseja pagar</p>
                  </div>
                </Label>
                
                {/* Sub-opcoes COD */}
                {paymentMethod === 'COD' && (
                  <div className="ml-4 pl-4 border-l-2 border-orange-300 space-y-3">
                    <div 
                      onClick={() => { setCodPaymentType('DINHEIRO'); }}
                      className={codPaymentType === 'DINHEIRO' ? 'flex items-center gap-3 p-3 border-2 border-green-500 bg-green-50 rounded-lg cursor-pointer transition-colors' : 'flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-slate-50 border-slate-200'}
                      data-testid="cod-dinheiro-option"
                    >
                      <div className={codPaymentType === 'DINHEIRO' ? 'w-5 h-5 rounded-full border-2 flex items-center justify-center border-green-500 bg-green-500' : 'w-5 h-5 rounded-full border-2 flex items-center justify-center border-slate-300'}>
                        {codPaymentType === 'DINHEIRO' && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <Banknote className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="font-medium text-sm">Dinheiro</p>
                      </div>
                    </div>
                    
                    {/* Troco */}
                    {codPaymentType === 'DINHEIRO' && (
                      <div className="ml-8 space-y-2">
                        <div className="flex items-center gap-2">
                          <input 
                            type="checkbox" 
                            id="needs-change"
                            checked={needsChange}
                            onChange={(e) => { setNeedsChange(e.target.checked); if (!e.target.checked) setChangeFor(''); }}
                            className="w-4 h-4 accent-green-600"
                            data-testid="needs-change-checkbox"
                          />
                          <label htmlFor="needs-change" className="text-sm text-slate-700 cursor-pointer">Preciso de troco</label>
                        </div>
                        {needsChange && (
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-slate-600">Troco para:</span>
                            <div className="relative">
                              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-500">R$</span>
                              <Input
                                type="number"
                                value={changeFor}
                                onChange={(e) => setChangeFor(e.target.value)}
                                placeholder="0,00"
                                className="pl-10 w-32"
                                min={0}
                                step="0.01"
                                data-testid="change-for-input"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div 
                      onClick={() => { setCodPaymentType('CARTAO_ENTREGA'); setNeedsChange(false); setChangeFor(''); }}
                      className={codPaymentType === 'CARTAO_ENTREGA' ? 'flex items-center gap-3 p-3 border-2 border-blue-500 bg-blue-50 rounded-lg cursor-pointer transition-colors' : 'flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-slate-50 border-slate-200'}
                      data-testid="cod-cartao-option"
                    >
                      <div className={codPaymentType === 'CARTAO_ENTREGA' ? 'w-5 h-5 rounded-full border-2 flex items-center justify-center border-blue-500 bg-blue-500' : 'w-5 h-5 rounded-full border-2 flex items-center justify-center border-slate-300'}>
                        {codPaymentType === 'CARTAO_ENTREGA' && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <CreditCard className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-sm">Cartao na Entrega</p>
                        <p className="text-xs text-slate-500">Credito ou Debito</p>
                      </div>
                    </div>


                    <div 
                      onClick={() => { setCodPaymentType('SODEXO'); setNeedsChange(false); setChangeFor(''); }}
                      className={codPaymentType === 'SODEXO' ? 'flex items-center gap-3 p-3 border-2 border-red-500 bg-red-50 rounded-lg cursor-pointer transition-colors' : 'flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-slate-50 border-slate-200'}
                      data-testid="cod-sodexo-option"
                    >
                      <div className={codPaymentType === 'SODEXO' ? 'w-5 h-5 rounded-full border-2 flex items-center justify-center border-red-500 bg-red-500' : 'w-5 h-5 rounded-full border-2 flex items-center justify-center border-slate-300'}>
                        {codPaymentType === 'SODEXO' && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <CreditCard className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="font-medium text-sm">Sodexo</p>
                      </div>
                    </div>

                    <div 
                      onClick={() => { setCodPaymentType('VR'); setNeedsChange(false); setChangeFor(''); }}
                      className={codPaymentType === 'VR' ? 'flex items-center gap-3 p-3 border-2 border-emerald-500 bg-emerald-50 rounded-lg cursor-pointer transition-colors' : 'flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-slate-50 border-slate-200'}
                      data-testid="cod-vr-option"
                    >
                      <div className={codPaymentType === 'VR' ? 'w-5 h-5 rounded-full border-2 flex items-center justify-center border-emerald-500 bg-emerald-500' : 'w-5 h-5 rounded-full border-2 flex items-center justify-center border-slate-300'}>
                        {codPaymentType === 'VR' && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <CreditCard className="w-5 h-5 text-emerald-600" />
                      <div>
                        <p className="font-medium text-sm">VR</p>
                      </div>
                    </div>

                    <div 
                      onClick={() => { setCodPaymentType('PLUXE'); setNeedsChange(false); setChangeFor(''); }}
                      className={codPaymentType === 'PLUXE' ? 'flex items-center gap-3 p-3 border-2 border-orange-500 bg-orange-50 rounded-lg cursor-pointer transition-colors' : 'flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-colors hover:bg-slate-50 border-slate-200'}
                      data-testid="cod-pluxe-option"
                    >
                      <div className={codPaymentType === 'PLUXE' ? 'w-5 h-5 rounded-full border-2 flex items-center justify-center border-orange-500 bg-orange-500' : 'w-5 h-5 rounded-full border-2 flex items-center justify-center border-slate-300'}>
                        {codPaymentType === 'PLUXE' && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <CreditCard className="w-5 h-5 text-orange-600" />
                      <div>
                        <p className="font-medium text-sm">Pluxe</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {paymentConfig?.pix_enabled && (
                  <Label
                    htmlFor="online_pix"
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      paymentMethod === 'ONLINE_PIX' ? 'border-orange-500 bg-orange-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <RadioGroupItem value="ONLINE_PIX" id="online_pix" />
                    <QrCode className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="font-medium">PIX Online</p>
                      <p className="text-sm text-slate-500">Pague agora via PIX</p>
                    </div>
                    <Badge className="ml-auto bg-green-100 text-green-700">Disponível</Badge>
                  </Label>
                )}
                
                {paymentConfig?.credit_card_enabled && (
                  <Label
                    htmlFor="online_card"
                    className={`flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors ${
                      paymentMethod === 'ONLINE_CARD' ? 'border-orange-500 bg-orange-50' : 'hover:bg-slate-50'
                    }`}
                  >
                    <RadioGroupItem value="ONLINE_CARD" id="online_card" />
                    <CreditCard className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="font-medium">Cartão de Crédito</p>
                      <p className="text-sm text-slate-500">Pague agora com cartão</p>
                    </div>
                    <Badge className="ml-auto bg-blue-100 text-blue-700">Disponível</Badge>
                  </Label>
                )}
                
                {/* Mostrar opções desabilitadas se não configurado */}
                {!paymentConfig?.pix_enabled && !paymentConfig?.credit_card_enabled && (
                  <div className="text-center py-4 text-slate-500 text-sm">
                    Pagamento online em breve disponível
                  </div>
                )}
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <CardTitle>Observações</CardTitle>
            </CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Alguma observação para o pedido? (opcional)"
                rows={3}
                data-testid="order-notes"
              />
            </CardContent>
          </Card>

          {/* Order Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="w-5 h-5" />
                Resumo do Pedido
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Items */}
              <div className="space-y-2">
                {cart.map((item) => (
                  <div key={item.product_id} className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      {item.quantity}x {item.name}
                    </span>
                    <span className="font-medium">{formatCurrency((item.price + (item.optionsTotal || 0)) * item.quantity)}</span>
                  </div>
                ))}
              </div>
              
              <Separator />
              
              {/* Totals */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Subtotal</span>
                  <span>{formatCurrency(pricing?.subtotal || getCartTotal())}</span>
                </div>
                
                {pricing?.coupon_discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Desconto ({pricing.coupon_code})</span>
                    <span>-{formatCurrency(pricing.coupon_discount)}</span>
                  </div>
                )}
                
                {orderType === 'DELIVERY' && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">
                      Taxa de entrega
                      {pricing?.delivery_time_estimate && (
                        <span className="text-xs text-slate-400 ml-2">
                          ({pricing.delivery_time_estimate})
                        </span>
                      )}
                    </span>
                    <span>
                      {pricing?.delivery_fee === 0 ? (
                        <span className="text-green-600">Grátis</span>
                      ) : (
                        formatCurrency(pricing?.delivery_fee || 0)
                      )}
                    </span>
                  </div>
                )}
              </div>
              
              <Separator />
              
              <div className="flex justify-between text-lg font-bold">
                <span>Total</span>
                <span style={{ color: menu?.theme_color }}>{formatCurrency(pricing?.total || getCartTotal())}</span>
              </div>
              
              {pricing?.errors?.length > 0 && (
                <div className="p-3 bg-red-50 rounded-lg">
                  {pricing.errors.map((error, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>{error}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Submit Button */}
          <Button
            type="submit"
            className="w-full"
            size="lg"
            style={{ backgroundColor: menu?.theme_color }}
            disabled={submitting || (pricing?.errors?.length > 0)}
            data-testid="submit-order-btn"
          >
            {submitting ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Processando...
              </>
            ) : (
              <>
                Finalizar Pedido - {formatCurrency(pricing?.total || getCartTotal())}
              </>
            )}
          </Button>
        </form>
      </main>

      {/* PIX Payment Modal */}
      <Dialog open={showPixModal} onOpenChange={setShowPixModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="w-5 h-5 text-green-600" />
              Pagamento PIX
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* QR Code */}
            {pixData?.qr_code_base64 && (
              <div className="flex justify-center">
                <img 
                  src={`data:image/png;base64,${pixData.qr_code_base64}`}
                  alt="QR Code PIX"
                  className="w-48 h-48"
                />
              </div>
            )}
            
            {/* Copy Code */}
            <div className="space-y-2">
              <Label>Código PIX (Copia e Cola)</Label>
              <div className="flex gap-2">
                <Input 
                  value={pixData?.qr_code || ''} 
                  readOnly 
                  className="text-xs"
                />
                <Button variant="outline" onClick={copyPixCode}>
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            {/* Instructions */}
            <div className="bg-green-50 p-4 rounded-lg space-y-2">
              <p className="text-sm font-medium text-green-800">Como pagar:</p>
              <ol className="text-sm text-green-700 space-y-1 list-decimal list-inside">
                <li>Abra o app do seu banco</li>
                <li>Escolha pagar com PIX</li>
                <li>Escaneie o QR Code ou cole o código</li>
                <li>Confirme o pagamento</li>
              </ol>
            </div>
            
            {/* Status */}
            <div className="flex items-center justify-center gap-2 text-slate-600">
              {checkingPixStatus ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Clock className="w-4 h-4" />
              )}
              <span className="text-sm">Aguardando pagamento...</span>
            </div>
            
            {/* Total */}
            <div className="text-center pt-2 border-t">
              <p className="text-lg font-bold" style={{ color: menu?.theme_color }}>
                Total: {formatCurrency(pricing?.total || getCartTotal())}
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Card Payment Modal */}
      <Dialog open={showCardModal} onOpenChange={(open) => {
        if (!processingCard) setShowCardModal(open);
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-blue-600" />
              Pagamento com Cartão
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Card Number */}
            <div className="space-y-2">
              <Label htmlFor="card-number">Número do Cartão</Label>
              <div className="relative">
                <Input
                  id="card-number"
                  placeholder="0000 0000 0000 0000"
                  value={cardData.number}
                  onChange={(e) => {
                    const formatted = formatCardNumber(e.target.value);
                    setCardData({...cardData, number: formatted});
                    const localBrand = detectCardBrand(e.target.value);
                    setCardBrand(localBrand);
                    if (binLookupTimer.current) clearTimeout(binLookupTimer.current);
                    binLookupTimer.current = setTimeout(() => lookupPaymentMethod(e.target.value), 150);
                  }}
                  maxLength={19}
                  className="pr-12"
                  disabled={processingCard || loadingBin}
                  data-testid="card-number-input"
                />
                {cardBrand && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <span className="text-xs font-semibold uppercase text-slate-500">
                      {cardBrand}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Card Holder Name */}
            <div className="space-y-2">
              <Label htmlFor="card-name">Nome no Cartão</Label>
              <Input
                id="card-name"
                placeholder="Como está impresso no cartão"
                value={cardData.name}
                onChange={(e) => setCardData({...cardData, name: e.target.value.toUpperCase()})}
                disabled={processingCard}
                data-testid="card-name-input"
              />
            </div>

            {/* Expiry and CVV */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="card-expiry">Validade</Label>
                <Input
                  id="card-expiry"
                  placeholder="MM/AA"
                  value={cardData.expiry}
                  onChange={(e) => setCardData({...cardData, expiry: formatExpiry(e.target.value)})}
                  maxLength={5}
                  disabled={processingCard}
                  data-testid="card-expiry-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="card-cvv">CVV</Label>
                <Input
                  id="card-cvv"
                  type="password"
                  placeholder="***"
                  value={cardData.cvv}
                  onChange={(e) => setCardData({...cardData, cvv: e.target.value.replace(/\D/g, '').substring(0, 4)})}
                  maxLength={4}
                  disabled={processingCard}
                  data-testid="card-cvv-input"
                />
              </div>
            </div>

            {/* CPF */}
            <div className="space-y-2">
              <Label htmlFor="card-cpf">CPF do Titular</Label>
              <Input
                id="card-cpf"
                placeholder="000.000.000-00"
                value={cardData.cpf}
                onChange={(e) => setCardData({...cardData, cpf: formatCPF(e.target.value)})}
                maxLength={14}
                disabled={processingCard}
                data-testid="card-cpf-input"
              />
            </div>

            {/* Installments */}
            <div className="space-y-2">
              <Label>Parcelas</Label>
              <Select
                value={String(cardData.installments)}
                onValueChange={(value) => setCardData({...cardData, installments: parseInt(value)})}
                disabled={processingCard}
              >
                <SelectTrigger data-testid="card-installments-select">
                  <SelectValue placeholder="Selecione as parcelas" />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}x de {formatCurrency((pricing?.total || getCartTotal()) / n)}
                      {n === 1 ? ' (à vista)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Security Notice */}
            <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 p-2 rounded">
              <Lock className="w-4 h-4" />
              <span>Pagamento seguro processado pelo Mercado Pago</span>
            </div>

            {/* Total */}
            <div className="text-center pt-2 border-t">
              <p className="text-lg font-bold" style={{ color: menu?.theme_color }}>
                Total: {formatCurrency(pricing?.total || getCartTotal())}
              </p>
            </div>

            {/* Submit Button */}
            <Button
              onClick={processCardPayment}
              className="w-full"
              style={{ backgroundColor: menu?.theme_color }}
              disabled={processingCard}
              data-testid="process-card-btn"
            >
              {processingCard ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Processando pagamento...
                </>
              ) : loadingBin ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Identificando cartao...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Pagar {formatCurrency(pricing?.total || getCartTotal())}
                </>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
